import React, { useState, useEffect } from 'react';
import { Product } from '@/types';
import { ProductCard } from './ProductCard';
import { getApiUrl } from '@/config';
import {
  MessageCircle,
  Gift,
  ShoppingBag,
  Star,
  CheckCircle,
  ArrowRight,
  Phone,
  Mail,
  MapPin,
  Sparkles,
  Users,
  FileText,
  Headphones,
} from 'lucide-react';

interface LandingPageProps {
  onNavigateToChat: () => void;
}

interface CategoryProduct {
  category: string;
  label: string;
  product: Product | null;
  icon: React.ReactNode;
}

export const LandingPage: React.FC<LandingPageProps> = ({ onNavigateToChat }) => {
  const [featuredProducts, setFeaturedProducts] = useState<Product[]>([]);
  const [categoryProducts, setCategoryProducts] = useState<CategoryProduct[]>([
    { category: 'tech', label: 'IT & Electronic', product: null, icon: <Headphones size={24} /> },
    { category: 'apparel', label: 'Apparel & Clothing', product: null, icon: <Gift size={24} /> },
    { category: 'bags', label: 'Bags & Totes', product: null, icon: <ShoppingBag size={24} /> },
    { category: 'stationery', label: 'Stationery', product: null, icon: <FileText size={24} /> },
  ]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchFeaturedProducts();
    fetchCategoryProducts();
  }, []);

  const fetchFeaturedProducts = async () => {
    try {
      const response = await fetch(getApiUrl('/api/featured-products'));
      if (response.ok) {
        const data = await response.json();
        setFeaturedProducts(data.products || []);
      }
    } catch (error) {
      console.error('Error fetching featured products:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchCategoryProducts = async () => {
    const categories = [
      { key: 'tech', label: 'Technology' },
      { key: 'apparel', label: 'Clothing' },
      { key: 'bags', label: 'Bags & Pouches' },
      { key: 'stationery', label: 'Organisers & Notebooks' },
    ];

    for (const cat of categories) {
      try {
        const response = await fetch(getApiUrl(`/api/products-by-label?label=${encodeURIComponent(cat.label)}&limit=1`));
        if (response.ok) {
          const data = await response.json();
          if (data.products && data.products.length > 0) {
            setCategoryProducts(prev => prev.map(cp =>
              cp.category === cat.key ? { ...cp, product: data.products[0] } : cp
            ));
          }
        }
      } catch (error) {
        console.error(`Error fetching ${cat.key} products:`, error);
      }
    }
  };

  const getProductImage = (product: Product | null): string | null => {
    if (!product) return null;
    if (Array.isArray(product.images) && product.images.length > 0) {
      return product.images[0];
    }
    if (typeof product.images === 'string' && product.images) {
      return product.images;
    }
    if (product.photo && !product.photo.includes('placeholder')) {
      return product.photo;
    }
    return null;
  };

  const handleProductClick = (product: Product) => {
    if (product.link) {
      window.open(product.link, '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <div className="min-h-screen bg-amber-50/50">
      {/* Header */}
      <header className="bg-white/90 backdrop-blur-sm shadow-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-gradient-to-br from-orange-500 to-amber-500 rounded-lg flex items-center justify-center">
                <Gift className="text-white" size={24} />
              </div>
              <div>
                <h1 className="font-bold text-xl text-gray-900">PrintnGift</h1>
                <p className="text-xs text-gray-500">Corporate Gifts Expert</p>
              </div>
            </div>
            <nav className="hidden md:flex items-center space-x-8">
              <a href="#products" className="text-gray-600 hover:text-orange-600 transition-colors">
                Products
              </a>
              <a href="#about" className="text-gray-600 hover:text-orange-600 transition-colors">
                About
              </a>
              <a href="#how-it-works" className="text-gray-600 hover:text-orange-600 transition-colors">
                How It Works
              </a>
              <a href="#contact" className="text-gray-600 hover:text-orange-600 transition-colors">
                Contact
              </a>
            </nav>
            <button
              onClick={onNavigateToChat}
              className="bg-gradient-to-r from-orange-500 to-amber-500 text-white px-4 py-2 rounded-lg hover:opacity-90 transition-opacity flex items-center space-x-2"
            >
              <MessageCircle size={18} />
              <span>Chat with Mary</span>
            </button>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-orange-400 via-amber-500 to-yellow-500 text-white overflow-hidden">
        <div className="absolute inset-0 bg-orange-600/10"></div>
        <div className="absolute top-0 right-0 w-96 h-96 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2"></div>
        <div className="absolute bottom-0 left-0 w-64 h-64 bg-white/10 rounded-full translate-y-1/2 -translate-x-1/2"></div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-32">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center space-x-2 bg-white bg-opacity-20 rounded-full px-4 py-2 mb-6">
                <Sparkles size={16} />
                <span className="text-sm">AI-Powered Sales Assistant</span>
              </div>
              <h1 className="text-4xl lg:text-5xl font-bold leading-tight mb-6">
                Your Corporate Gifts Expert is Here to Help
              </h1>
              <p className="text-lg lg:text-xl opacity-90 mb-8">
                Chat with Mary, our AI sales assistant, to find the perfect corporate gifts for any occasion.
                Get personalized recommendations and instant quotations.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <button
                  onClick={onNavigateToChat}
                  className="bg-white text-orange-600 px-8 py-4 rounded-xl font-semibold hover:bg-orange-50 transition-colors flex items-center justify-center space-x-2 shadow-lg"
                >
                  <MessageCircle size={20} />
                  <span>Start Chatting with Mary</span>
                  <ArrowRight size={20} />
                </button>
                <a
                  href="#products"
                  className="border-2 border-white text-white px-8 py-4 rounded-xl font-semibold hover:bg-white hover:text-orange-600 transition-colors flex items-center justify-center space-x-2"
                >
                  <ShoppingBag size={20} />
                  <span>Browse Products</span>
                </a>
              </div>
            </div>

            {/* Hero Category Cards with Product Images */}
            <div className="hidden lg:grid grid-cols-2 gap-6">
              {categoryProducts.map((cat) => {
                const imageUrl = getProductImage(cat.product);
                return (
                  <div
                    key={cat.category}
                    className="bg-white/10 backdrop-blur-sm rounded-2xl overflow-hidden hover:bg-white/20 transition-all cursor-pointer group"
                    onClick={onNavigateToChat}
                  >
                    {imageUrl ? (
                      <div className="h-28 overflow-hidden">
                        <img
                          src={imageUrl}
                          alt={cat.label}
                          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                          onError={(e) => {
                            const target = e.target as HTMLImageElement;
                            target.style.display = 'none';
                          }}
                        />
                      </div>
                    ) : (
                      <div className="h-28 flex items-center justify-center bg-white/10">
                        <div className="w-16 h-16 bg-white/20 rounded-xl flex items-center justify-center">
                          {cat.icon}
                        </div>
                      </div>
                    )}
                    <div className="p-4">
                      <h3 className="font-semibold text-lg">{cat.label}</h3>
                      <p className="text-sm opacity-80">Browse our collection</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Featured Products Section */}
      <section id="products" className="py-16 lg:py-24 bg-orange-50/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Featured Products
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Discover our most popular corporate gifts, perfect for any occasion.
            </p>
          </div>

          {isLoading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="bg-gray-100 rounded-xl animate-pulse h-80"></div>
              ))}
            </div>
          ) : featuredProducts.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {featuredProducts.slice(0, 8).map((product, index) => (
                <ProductCard
                  key={product.id || index}
                  product={product}
                  onClick={handleProductClick}
                  variant="default"
                />
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <ShoppingBag size={48} className="mx-auto text-gray-300 mb-4" />
              <p className="text-gray-500">No featured products available at the moment.</p>
            </div>
          )}

          <div className="text-center mt-10">
            <button
              onClick={onNavigateToChat}
              className="inline-flex items-center space-x-2 bg-orange-500 text-white px-6 py-3 rounded-lg hover:bg-orange-600 transition-colors"
            >
              <MessageCircle size={20} />
              <span>Ask Mary for More Recommendations</span>
            </button>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-16 lg:py-24 bg-amber-50/70">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6">
                About PrintnGift
              </h2>
              <p className="text-lg text-gray-600 mb-6">
                PrintnGift is your trusted partner for corporate gifting solutions in Singapore.
                With years of experience in the industry, we understand the importance of making
                lasting impressions through thoughtful, high-quality gifts.
              </p>
              <p className="text-lg text-gray-600 mb-8">
                Our AI-powered assistant Mary is here to help you find the perfect gifts for
                your team, clients, or special occasions. From premium drinkware to custom
                branded merchandise, we have something for every budget and requirement.
              </p>
              <div className="grid grid-cols-2 gap-6">
                <div className="flex items-start space-x-3">
                  <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Star className="text-orange-600" size={20} />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900">Premium Quality</h4>
                    <p className="text-sm text-gray-600">Only the best products for your brand</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <CheckCircle className="text-orange-600" size={20} />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900">Trusted Service</h4>
                    <p className="text-sm text-gray-600">Reliable delivery and support</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Gift className="text-orange-600" size={20} />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900">Custom Branding</h4>
                    <p className="text-sm text-gray-600">Personalize gifts with your logo</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <ShoppingBag className="text-orange-600" size={20} />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900">Bulk Orders</h4>
                    <p className="text-sm text-gray-600">Special pricing for large quantities</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="relative">
              <div className="bg-gradient-to-br from-orange-100 to-amber-100 rounded-3xl p-8 lg:p-12">
                <div className="bg-white rounded-2xl shadow-xl p-6">
                  <div className="flex items-center space-x-3 mb-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-amber-500 rounded-full flex items-center justify-center">
                      <MessageCircle className="text-white" size={24} />
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900">Mary</h4>
                      <p className="text-sm text-gray-500">Your AI Sales Assistant</p>
                    </div>
                  </div>
                  <div className="space-y-3">
                    <div className="bg-orange-50 rounded-lg rounded-tl-none p-3">
                      <p className="text-sm text-gray-700">
                        Hi! I'm Mary, your corporate gifts expert. How can I help you today?
                      </p>
                    </div>
                    <div className="bg-orange-500 text-white rounded-lg rounded-tr-none p-3 ml-8">
                      <p className="text-sm">
                        I need gifts for 50 employees under $30 each
                      </p>
                    </div>
                    <div className="bg-orange-50 rounded-lg rounded-tl-none p-3">
                      <p className="text-sm text-gray-700">
                        Great! I have some excellent options for you. Let me show you our popular choices...
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section id="how-it-works" className="py-16 lg:py-24 bg-white/80">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              How It Works
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Getting the perfect corporate gifts is as easy as having a conversation.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-20 h-20 bg-gradient-to-br from-orange-500 to-amber-500 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg">
                <MessageCircle className="text-white" size={36} />
              </div>
              <div className="bg-orange-100 text-orange-700 text-sm font-semibold px-3 py-1 rounded-full inline-block mb-4">
                Step 1
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">
                Chat with Mary
              </h3>
              <p className="text-gray-600">
                Start a conversation with our AI assistant. Tell her about your gifting needs,
                budget, and occasion.
              </p>
            </div>

            <div className="text-center">
              <div className="w-20 h-20 bg-gradient-to-br from-orange-500 to-amber-500 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg">
                <Gift className="text-white" size={36} />
              </div>
              <div className="bg-orange-100 text-orange-700 text-sm font-semibold px-3 py-1 rounded-full inline-block mb-4">
                Step 2
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">
                Get Recommendations
              </h3>
              <p className="text-gray-600">
                Receive personalized product suggestions based on your requirements.
                Browse and compare options easily.
              </p>
            </div>

            <div className="text-center">
              <div className="w-20 h-20 bg-gradient-to-br from-orange-500 to-amber-500 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg">
                <FileText className="text-white" size={36} />
              </div>
              <div className="bg-orange-100 text-orange-700 text-sm font-semibold px-3 py-1 rounded-full inline-block mb-4">
                Step 3
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">
                Receive Quotation
              </h3>
              <p className="text-gray-600">
                Get a detailed quotation with pricing, delivery options, and customization details
                sent directly to your email.
              </p>
            </div>
          </div>

          <div className="text-center mt-12">
            <button
              onClick={onNavigateToChat}
              className="bg-gradient-to-r from-orange-500 to-amber-500 text-white px-8 py-4 rounded-xl font-semibold hover:opacity-90 transition-opacity flex items-center space-x-2 mx-auto shadow-lg"
            >
              <MessageCircle size={20} />
              <span>Start Your Gift Journey</span>
              <ArrowRight size={20} />
            </button>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 lg:py-20 bg-gradient-to-r from-orange-500 to-amber-500 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl lg:text-4xl font-bold mb-6">
            Ready to Find the Perfect Gifts?
          </h2>
          <p className="text-lg lg:text-xl opacity-90 mb-8">
            Start chatting with Mary now and discover corporate gifts that make a lasting impression.
          </p>
          <button
            onClick={onNavigateToChat}
            className="bg-white text-orange-600 px-8 py-4 rounded-xl font-semibold hover:bg-orange-50 transition-colors inline-flex items-center space-x-2 shadow-lg"
          >
            <MessageCircle size={20} />
            <span>Chat with Mary Now</span>
            <ArrowRight size={20} />
          </button>
        </div>
      </section>

      {/* Footer */}
      <footer id="contact" className="bg-gray-900 text-white py-12 lg:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8">
            <div className="md:col-span-2">
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-10 h-10 bg-gradient-to-br from-orange-500 to-amber-500 rounded-lg flex items-center justify-center">
                  <Gift className="text-white" size={24} />
                </div>
                <div>
                  <h3 className="font-bold text-xl">PrintnGift</h3>
                  <p className="text-sm text-gray-400">Corporate Gifts Expert</p>
                </div>
              </div>
              <p className="text-gray-400 mb-6 max-w-md">
                Your trusted partner for corporate gifting solutions. Premium quality products
                with personalized service to make every occasion special.
              </p>
              <button
                onClick={onNavigateToChat}
                className="bg-orange-500 text-white px-6 py-2 rounded-lg hover:bg-orange-600 transition-colors inline-flex items-center space-x-2"
              >
                <MessageCircle size={18} />
                <span>Chat with Mary</span>
              </button>
            </div>

            <div>
              <h4 className="font-semibold text-lg mb-4">Quick Links</h4>
              <ul className="space-y-2">
                <li>
                  <a href="#products" className="text-gray-400 hover:text-white transition-colors">
                    Products
                  </a>
                </li>
                <li>
                  <a href="#about" className="text-gray-400 hover:text-white transition-colors">
                    About Us
                  </a>
                </li>
                <li>
                  <a href="#how-it-works" className="text-gray-400 hover:text-white transition-colors">
                    How It Works
                  </a>
                </li>
                <li>
                  <button
                    onClick={onNavigateToChat}
                    className="text-gray-400 hover:text-white transition-colors"
                  >
                    Sales Assistant
                  </button>
                </li>
              </ul>
            </div>

            <div>
              <h4 className="font-semibold text-lg mb-4">Contact Us</h4>
              <ul className="space-y-3">
                <li className="flex items-center space-x-3">
                  <Phone size={18} className="text-orange-400" />
                  <span className="text-gray-400">+65 6123 4567</span>
                </li>
                <li className="flex items-center space-x-3">
                  <Mail size={18} className="text-orange-400" />
                  <span className="text-gray-400">sales@printngift.com</span>
                </li>
                <li className="flex items-start space-x-3">
                  <MapPin size={18} className="text-orange-400 flex-shrink-0 mt-1" />
                  <span className="text-gray-400">
                    123 Corporate Way<br />
                    Singapore 123456
                  </span>
                </li>
              </ul>
            </div>
          </div>

          <div className="border-t border-gray-800 mt-12 pt-8 text-center">
            <p className="text-gray-400 text-sm">
              &copy; {new Date().getFullYear()} PrintnGift. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};
